import type { BriefDocument, BriefFieldValue } from '../types/chat';

const IGNORED_KEYS = new Set(['workId']);

function hasValue(field: BriefFieldValue): boolean {
    if (field.text != null && field.text !== '') return true;
    if (field.multiText != null && field.multiText.length > 0) return true;
    if (field.integer != null) return true;
    if (field.date != null) return true;
    if (field.boolean != null) return true;
    if (field.choice != null && field.choice !== '') return true;
    if (field.multiChoice != null && field.multiChoice.length > 0) return true;
    if (field.files != null && field.files.length > 0) return true;
    return false;
}

export function isDocumentComplete(document: BriefDocument | null | undefined): boolean {
    if (!document?.schema?.fields) {
        return false;
    }

    const requiredKeys = document.schema.fields
        .filter((f) => f.required && !IGNORED_KEYS.has(f.key))
        .map((f) => f.key);

    if (requiredKeys.length === 0) {
        return false;
    }

    const valuesByKey = new Map(document.values.map((v) => [v.key, v]));

    const missingKeys = requiredKeys.filter((key) => {
        const value = valuesByKey.get(key);
        return !(value != null && hasValue(value));
    });

    return missingKeys.length === 0;
}

export interface DynamicFieldCompletionInput {
    __typename: string;
    id: string;
    required?: boolean | null;
    inputValue?: string | null;
    textAreaValue?: string | null;
    choiceValue?: string | null;
    multiInputValue?: string[] | null;
    multiChoiceValue?: string[] | null;
    multiChoiceValues?: string[] | null;
    multiGridChoiceValue?: string[] | null;
    booleanValue?: boolean | null;
    dateValue?: string | null;
    filesValue?: unknown[] | null;
    workEntityValue?: string | null;
    clientConfiguredValue?: string | null;
}

export function isDynamicFieldsComplete(
    fields: readonly DynamicFieldCompletionInput[] | null | undefined
): boolean {
    if (!fields || fields.length === 0) {
        return false;
    }

    const requiredFields = fields.filter((f) => f.required && !IGNORED_KEYS.has(f.id));

    if (requiredFields.length === 0) {
        return false;
    }

    return requiredFields.every((f) => dynamicFieldHasValue(f));
}

function dynamicFieldHasValue(f: DynamicFieldCompletionInput): boolean {
    if (f.inputValue != null && f.inputValue !== '') return true;
    if (f.textAreaValue != null && f.textAreaValue !== '') return true;
    if (f.choiceValue != null && f.choiceValue !== '') return true;
    if (f.multiInputValue != null && f.multiInputValue.length > 0) return true;
    if (f.multiChoiceValue != null && f.multiChoiceValue.length > 0) return true;
    if (f.multiChoiceValues != null && f.multiChoiceValues.length > 0) return true;
    if (f.multiGridChoiceValue != null && f.multiGridChoiceValue.length > 0) return true;
    if (f.booleanValue != null) return true;
    if (f.dateValue != null && f.dateValue !== '') return true;
    if (f.filesValue != null && f.filesValue.length > 0) return true;
    if (f.workEntityValue != null && f.workEntityValue !== '') return true;
    if (f.clientConfiguredValue != null && f.clientConfiguredValue !== '') return true;
    return false;
}
